import { z } from 'zod';
/**
 * Wallet — schemi per operazioni su `wallet_transactions`.
 *
 * Append-only: ogni mutation (deposit/debit/refund) crea una nuova riga.
 * Saldo = SUM(amount_cents). Nessun update, nessuna delete.
 *
 * Concorrenza: gestita a livello servizio con `SELECT ... FOR UPDATE` +
 * Prisma `$transaction` (pattern portato da Spike 004).
 *
 * Idempotency: tramite `idempotencyKey` (persistita come `related_id`) +
 * partial UNIQUE index `wallet_tx_idempotency_uidx` creato nella migration.
 */
/* -------------------------------------------------------------------------- */
/* WalletReason — motivo della transazione                                    */
/* -------------------------------------------------------------------------- */
export const WalletReason = {
    DEPOSIT_STRIPE: 'DEPOSIT_STRIPE',
    DEPOSIT_MANUAL: 'DEPOSIT_MANUAL',
    DEBIT_PRATICA: 'DEBIT_PRATICA',
    DEBIT_SERVIZIO: 'DEBIT_SERVIZIO',
    REFUND_PRATICA: 'REFUND_PRATICA',
    FIDO_ADJUSTMENT: 'FIDO_ADJUSTMENT',
    RECONCILE: 'RECONCILE',
};
export const walletReasonSchema = z.nativeEnum(WalletReason);
const depositReasonSchema = z.enum(['DEPOSIT_STRIPE', 'DEPOSIT_MANUAL']);
const debitReasonSchema = z.enum(['DEBIT_PRATICA', 'DEBIT_SERVIZIO']);
const refundReasonSchema = z.enum(['REFUND_PRATICA']);
/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */
/** cuid-like identifier (Better Auth + Prisma). Accetta anche UUID generici. */
const accountIdSchema = z
    .string({ required_error: 'accountId obbligatorio' })
    .trim()
    .min(1, { message: 'accountId obbligatorio' });
const idempotencyKeySchema = z
    .string()
    .trim()
    .min(8, { message: 'idempotencyKey deve essere lungo almeno 8 caratteri' })
    .max(255, { message: 'idempotencyKey troppo lungo' });
const metadataSchema = z.record(z.string(), z.unknown()).optional();
/**
 * Importo positivo in cents. Sempre BigInt per evitare overflow e
 * disallineamenti con il tipo Prisma (`BigInt`).
 *
 * Zod accetta `number | bigint | string` in input e coerce-a BigInt solo se
 * il valore rappresenta un intero positivo.
 */
const positiveAmountCentsSchema = z
    .union([z.bigint(), z.number().int(), z.string().regex(/^[0-9]+$/)])
    .transform((v, ctx) => {
    try {
        const bi = typeof v === 'bigint' ? v : BigInt(v);
        return bi;
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'amountCents non numerico' });
        return z.NEVER;
    }
})
    .refine((v) => v > 0n, { message: 'amountCents deve essere strettamente positivo' });
/* -------------------------------------------------------------------------- */
/* Deposit / Debit / Refund inputs                                             */
/* -------------------------------------------------------------------------- */
export const depositInputSchema = z.object({
    accountId: accountIdSchema,
    amountCents: positiveAmountCentsSchema,
    reason: depositReasonSchema,
    idempotencyKey: idempotencyKeySchema.optional(),
    actorId: z.string().trim().optional(),
    metadata: metadataSchema,
});
export const debitInputSchema = z.object({
    accountId: accountIdSchema,
    amountCents: positiveAmountCentsSchema,
    reason: debitReasonSchema,
    idempotencyKey: idempotencyKeySchema.optional(),
    actorId: z.string().trim().optional(),
    metadata: metadataSchema,
});
export const refundInputSchema = z.object({
    accountId: accountIdSchema,
    amountCents: positiveAmountCentsSchema,
    reason: refundReasonSchema,
    idempotencyKey: idempotencyKeySchema.optional(),
    actorId: z.string().trim().optional(),
    metadata: metadataSchema,
});
/* -------------------------------------------------------------------------- */
/* Discriminated union per dispatcher applicativo                              */
/* -------------------------------------------------------------------------- */
const depositDiscriminatedSchema = depositInputSchema.extend({
    operation: z.literal('deposit'),
});
const debitDiscriminatedSchema = debitInputSchema.extend({
    operation: z.literal('debit'),
});
const refundDiscriminatedSchema = refundInputSchema.extend({
    operation: z.literal('refund'),
});
export const walletOperationInputSchema = z.discriminatedUnion('operation', [
    depositDiscriminatedSchema,
    debitDiscriminatedSchema,
    refundDiscriminatedSchema,
]);
/* -------------------------------------------------------------------------- */
/* HTTP boundary — F-009.2 ricarica wallet (Stripe Checkout)                   */
/* -------------------------------------------------------------------------- */
/**
 * Taglio minimo di ricarica (BR-3 UC-010) in cents. Default di sistema €10.
 * Esportato così il frontend può mostrarlo/validarlo lato client. I tagli
 * suggeriti (€50/100/250/500) sono UI-only; qui imponiamo solo il minimo.
 */
export const MIN_RICARICA_CENTS = 1000n;
/**
 * Body di `POST /api/v1/wallet/ricarica`. L'utente sceglie l'importo (preset o
 * custom): validiamo che sia un intero positivo ≥ minimo. La creazione della
 * Stripe Checkout Session avviene server-side; il saldo NON viene toccato qui
 * — l'accredito avviene solo alla conferma webhook (UC-010 step 7, BR-2).
 */
export const ricaricaRequestSchema = z.object({
    amountCents: positiveAmountCentsSchema.refine((v) => v >= MIN_RICARICA_CENTS, {
        message: `L'importo minimo di ricarica è ${(Number(MIN_RICARICA_CENTS) / 100).toFixed(2)} €`,
    }),
});
/** Risposta di `POST /api/v1/wallet/ricarica`: URL della pagina Stripe Checkout. */
export const ricaricaResponseSchema = z.object({
    checkoutUrl: z.string().url(),
    sessionId: z.string(),
});
/* -------------------------------------------------------------------------- */
/* HTTP boundary — F-009 wallet API (read side)                                */
/* -------------------------------------------------------------------------- */
/**
 * Risposta del summary wallet (`GET /api/v1/wallet`).
 *
 * I campi monetari sono `BigInt` lato servizio (cents) ma JSON non serializza
 * BigInt nativamente: al boundary HTTP vengono convertiti a **stringa**
 * (coerente con il pattern wallet/cents del codebase, cfr. admin/clienti).
 * Il client riconverte con `BigInt(value)` o `Number(value)` dove sicuro.
 *
 * - `balanceCents`     saldo = SUM(amount_cents) sui movimenti confermati.
 * - `fidoLimitCents`   limite di overdraft concesso dall'admin (≥ 0).
 * - `availableCents`   disponibilità effettiva = balance + fido.
 * - `walletBlocked`    submit pratica disabilitato finché true.
 * - `gracePeriodUntil` scadenza grace dopo riduzione fido con sconfinamento.
 */
export const walletSummaryResponseSchema = z.object({
    balanceCents: z.string(),
    fidoLimitCents: z.string(),
    availableCents: z.string(),
    walletBlocked: z.boolean(),
    gracePeriodUntil: z.string().nullable(),
});
/**
 * Query string per lo storico transazioni (`GET /api/v1/wallet/transactions`).
 * Paginazione offset-based (page/limit) + filtri opzionali per tipo movimento
 * e intervallo date. `.strict()` rifiuta chiavi sconosciute (no silent typo).
 */
export const walletTransactionsQuerySchema = z
    .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    reason: walletReasonSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
})
    .strict();
/** Singola riga dello storico (boundary HTTP, BigInt→string). */
export const walletTransactionItemSchema = z.object({
    id: z.string(),
    amountCents: z.string(),
    reason: z.string(),
    relatedId: z.string().nullable(),
    actorType: z.string(),
    createdAt: z.string(),
    metadata: z.unknown().nullable().optional(),
});
/** Risposta paginata dello storico transazioni. */
export const walletTransactionsResponseSchema = z.object({
    items: z.array(walletTransactionItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
});
//# sourceMappingURL=wallet.js.map